import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import CustomFieldTypes from '@/constants/customFields/CustomFieldTypes';
import FilterOperator from '@/constants/forms/FilterOperator';
import type AdvancedConstituentSearchFilter from '@/types/AdvancedConstituentSearchFilter';
import type CustomField from '@/types/api/CustomField';
import type SearchConstituent from '@/types/api/SearchConstituent';
import type Label from '@/types/Label';
import { formatDateToString } from '@/utils/dates';
import type AdvancedFilterFormFilter from '../types/AdvancedFilterFormFilter';
import isMoneyField from './isMoneyField';

const { IS_NOT_NULL, IS_NULL, BETWEEN } = FilterOperator;
const {
  DOB,
  CONNECTION_EMPLOYER_CONSTITUENT_IDS,
  CONNECTION_HOUSEHOLD_CONSTITUENT_IDS,
  CONNECTION_CONSTITUENT_IDS,
  CONSTITUENT_LABEL_IDS,
  DONATION_LIFETIME_TOTAL_AMOUNT,
  DONATION_THIS_YEAR_TOTAL_AMOUNT,
  DONATION_LAST_YEAR_TOTAL_AMOUNT,
  DONATION_AMOUNT,
  DONATION_LIFETIME_TOTAL_TIME,
  DONATION_THIS_YEAR_TOTAL_TIME,
  DONATION_LAST_YEAR_TOTAL_TIME,
  DONATION_MINUTES,
  IN_HONOR,
  DONATION_DATE,
  DONATION_LABEL_IDS,
} = ConstituentSearchFilterField;

const mapAdvancedFormDataToFilters = (
  formData: {
    advancedFilters: AdvancedFilterFormFilter[];
  },
  customFields: CustomField[] = [],
) => ({
  advancedFilters: formData.advancedFilters.reduce<
    AdvancedConstituentSearchFilter[]
  >((filters, filter) => {
    const { field, operator, value } = filter;

    // These should never be triggered based on react-hook-form validation,
    // but we want to be notified if errors arise. This also allows us to
    // more safely cast types below.
    if (!field) {
      throw new Error('`field` missing from query builder form data');
    }
    if (!operator) {
      throw new Error('`operator` missing from query builder form data');
    }

    const matchedCustomField = customFields.find(
      (item) => item.customFieldId === field,
    );

    // These operators do not need a value
    if (operator === IS_NOT_NULL || operator === IS_NULL) {
      const newFilter: AdvancedConstituentSearchFilter = { field, operator };
      if (matchedCustomField) {
        newFilter.isCustomField = true;
      }
      return [...filters, newFilter];
    }

    if (
      field === DOB ||
      field === DONATION_DATE ||
      matchedCustomField?.fieldType === CustomFieldTypes.DATE_PICKER
    ) {
      const newFilter: AdvancedConstituentSearchFilter = { field, operator };

      if (matchedCustomField) {
        newFilter.isCustomField = true;
      }

      if (!Array.isArray(value) || !value.length) {
        throw new Error(
          `invalid 'value' for ${field} in query builder form data`,
        );
      }

      if (operator === BETWEEN) {
        if (value.length !== 2) {
          throw new Error(
            `invalid 'value' for ${field} in query builder form data`,
          );
        }

        return [
          ...filters,
          {
            ...newFilter,
            value: value.map(formatDateToString),
          },
        ];
      }
      return [
        ...filters,
        {
          ...newFilter,
          value: formatDateToString(value[0]),
        },
      ];
    }

    const { isCustomField, ...newFilter } = filter;
    switch (field) {
      case CONSTITUENT_LABEL_IDS:
      case DONATION_LABEL_IDS: {
        return [
          ...filters,
          {
            ...newFilter,
            value: (value as Label[]).map((item): string => item.labelId),
          } as AdvancedConstituentSearchFilter,
        ];
      }
      case IN_HONOR: {
        const { constituentId, displayName } = value as SearchConstituent;
        return [
          ...filters,
          {
            field,
            operator,
            value: constituentId || displayName,
          },
        ];
      }
      case CONNECTION_CONSTITUENT_IDS:
      case CONNECTION_EMPLOYER_CONSTITUENT_IDS:
      case CONNECTION_HOUSEHOLD_CONSTITUENT_IDS:
        return [
          ...filters,
          {
            field,
            operator,
            value: (value as SearchConstituent).constituentId,
          },
        ];
      // These six fields are mapped the same way, except the multiplier is different
      // for time and money donations.
      case DONATION_LIFETIME_TOTAL_AMOUNT:
      case DONATION_THIS_YEAR_TOTAL_AMOUNT:
      case DONATION_LAST_YEAR_TOTAL_AMOUNT:
      case DONATION_AMOUNT:
      case DONATION_LIFETIME_TOTAL_TIME:
      case DONATION_THIS_YEAR_TOTAL_TIME:
      case DONATION_LAST_YEAR_TOTAL_TIME:
      case DONATION_MINUTES: {
        const multiplier = isMoneyField(field) ? 100 : 60;

        // Cases where the data is invalid
        if (
          !Array.isArray(value) ||
          !value.length ||
          value.some((val) => typeof Number(val) !== 'number') ||
          (operator === BETWEEN && value.length !== 2)
        ) {
          throw new Error(
            `invalid 'value' for ${field} in query builder form data`,
          );
        }

        return [
          ...filters,
          {
            ...newFilter,
            value:
              operator === BETWEEN
                ? value.map((val) => Number(val) * multiplier)
                : Number(value[0]) * multiplier,
          } as AdvancedConstituentSearchFilter,
        ];
      }
      default:
        return [
          ...filters,
          (matchedCustomField
            ? {
                ...newFilter,
                isCustomField: true,
              }
            : filter) as AdvancedConstituentSearchFilter,
        ];
    }
  }, []),
});

export default mapAdvancedFormDataToFilters;
